/** Single leg inside a multi-leg trip (inicio/fin in app-local ISO format). */
export type TripLeg = {
  /**
   * ISO-like datetime string in app timezone (Europe/Madrid).
   * Format: 'YYYY-MM-DDTHH:mm' (no seconds, no timezone suffix).
   */
  inicio: string;
  /**
   * Same format as `inicio`.
   */
  fin: string;
  tipo: 'Internacional' | 'Nacional';
  region?: string; // optional region for Nacional legs
  horas?: number; // optional cached value
  pago?: number; // optional cached value
};

// Backward compatible alias kept while migrating to explicit TripLeg naming.
export type Leg = TripLeg;

// Legacy single-range trip kept for backward compatibility
export type LegacyTrip = {
  /**
   * ISO-like datetime string in app timezone (Europe/Madrid).
   * Format: 'YYYY-MM-DDTHH:mm' (no seconds, no timezone suffix).
   */
  inicio: string;
  /**
   * Same format as `inicio`.
   */
  fin: string;
  tipo: 'Internacional' | 'Nacional';
  horas?: number;
  pago?: number;
  // optional cached summaries used by UI/hooks
  fullWorkingDays?: number;
  paidMeals?: PaidMeal[];
};

export type MultiLegTrip = {
  // unique id could be added later
  id?: string;
  /**
   * Array of trip legs, each with canonical datetime format for inicio/fin.
   */
  legs: TripLeg[];
  notas?: string;
  // Convenio cached summaries
  fullWorkingDays?: number;
  paidMeals?: PaidMeal[];
  // optional cached totals
  pago?: number;
  horas?: number;
};

export type Trip = MultiLegTrip | (LegacyTrip & { id?: string });

export type PaidMeal = {
  fecha: string;
  meal: 'breakfast' | 'lunch' | 'dinner' | string;
  inicio?: string;
  fin?: string;
  pago: number;
  tipo?: 'Internacional' | 'Nacional';
};

// Type guards for Trip union
export function isMultiLegTrip(trip: Trip): trip is MultiLegTrip {
  return 'legs' in trip && Array.isArray(trip.legs);
}

export function isLegacyTrip(trip: Trip): trip is LegacyTrip & { id?: string } {
  return !('legs' in trip) && 'inicio' in trip && 'fin' in trip;
}

// Helper functions to safely access Trip properties
export function getTripInicio(trip: Trip): string {
  if (isMultiLegTrip(trip)) {
    return trip.legs[0]?.inicio ?? '';
  }
  return trip.inicio;
}

export function getTripFin(trip: Trip): string {
  if (isMultiLegTrip(trip)) {
    return trip.legs[trip.legs.length - 1]?.fin ?? '';
  }
  return trip.fin;
}

export function getTripTipo(trip: Trip): 'Internacional' | 'Nacional' {
  if (isMultiLegTrip(trip)) {
    return trip.legs[0]?.tipo ?? 'Internacional';
  }
  return trip.tipo;
}

export function getTripLegs(trip: Trip): TripLeg[] {
  if (isMultiLegTrip(trip)) {
    return trip.legs;
  }
  return [{ inicio: trip.inicio, fin: trip.fin, tipo: trip.tipo }];
}
